import type {
  ClientDocument,
  DocumentFolder,
  DocumentListParams,
  DocumentSource,
  DocumentStatus,
} from "@/features/documents/types";
import { apiFetch, apiFetchBlob, apiFetchResponse } from "@/lib/api";
import { downloadBlob } from "@/lib/download";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiDocument {
  id: string;
  client: string;
  client_name: string;
  case: string | null;
  folder: string | null;
  name: string;
  source: DocumentSource;
  source_display: string;
  status: DocumentStatus;
  status_display: string;
  form_number: string;
  version: number;
  version_group: string;
  content_type: string;
  size_bytes: number;
  download_path: string;
  is_deleted: boolean;
  deleted_at: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  is_published: boolean;
  proforma_status: string | null;
  proforma_status_display: string | null;
  created_at: string;
  updated_at: string;
}

function toDocument(api: ApiDocument): ClientDocument {
  return {
    id: api.id,
    client: api.client,
    clientName: api.client_name,
    case: api.case,
    folder: api.folder,
    name: api.name,
    source: api.source,
    sourceDisplay: api.source_display,
    status: api.status,
    statusDisplay: api.status_display,
    formNumber: api.form_number,
    version: api.version,
    versionGroup: api.version_group,
    contentType: api.content_type,
    sizeBytes: api.size_bytes,
    downloadPath: api.download_path,
    isDeleted: api.is_deleted,
    deletedAt: api.deleted_at,
    uploadedBy: api.uploaded_by,
    uploadedByName: api.uploaded_by_name,
    isPublished: api.is_published,
    proformaStatus: api.proforma_status,
    proformaStatusDisplay: api.proforma_status_display,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/** GET /documents/ — a client's documents, filtered/sorted by the table controls. */
export async function listClientDocuments(
  clientId: string,
  params: DocumentListParams = {},
): Promise<ClientDocument[]> {
  const qs = new URLSearchParams({ page_size: "200", client: clientId });
  if (params.deleted) qs.set("deleted", "true");
  if (params.folderId) qs.set("folder", params.folderId);
  if (params.status) qs.set("status", params.status);
  if (params.search) qs.set("search", params.search);
  if (params.ordering) qs.set("ordering", params.ordering);
  if (params.year !== undefined) qs.set("year", String(params.year));
  const body = await apiFetch<Envelope<ApiDocument[]>>(`/documents/?${qs.toString()}`);
  return body.data.map(toDocument);
}

/** POST /documents/ — multipart upload, optionally into a folder. */
export async function uploadDocument(
  clientId: string,
  file: File,
  options: { name?: string; folderId?: string | null } = {},
): Promise<ClientDocument> {
  const form = new FormData();
  form.append("client", clientId);
  form.append("file", file);
  if (options.name) form.append("name", options.name);
  if (options.folderId) form.append("folder", options.folderId);
  const body = await apiFetch<Envelope<ApiDocument>>("/documents/", {
    method: "POST",
    form,
  });
  return toDocument(body.data);
}

/** DELETE /documents/:id/ — soft-delete (moves to Recently Deleted). */
export function deleteDocument(id: string): Promise<void> {
  return apiFetch<void>(`/documents/${id}/`, { method: "DELETE" });
}

/** PATCH /documents/:id/ — move to a folder (null = root) and/or rename. */
export async function updateDocument(
  id: string,
  changes: { folder?: string | null; name?: string },
): Promise<ClientDocument> {
  const body = await apiFetch<Envelope<ApiDocument>>(`/documents/${id}/`, {
    method: "PATCH",
    json: changes,
  });
  return toDocument(body.data);
}

export function moveDocument(id: string, folderId: string | null) {
  return updateDocument(id, { folder: folderId });
}

export function renameDocument(id: string, name: string) {
  return updateDocument(id, { name });
}

/** POST /documents/:id/restore/ — undelete. */
export async function restoreDocument(id: string): Promise<ClientDocument> {
  const body = await apiFetch<Envelope<ApiDocument>>(`/documents/${id}/restore/`, {
    method: "POST",
  });
  return toDocument(body.data);
}

/** DELETE /documents/:id/purge/ — permanently delete file + row. */
export function purgeDocument(id: string): Promise<void> {
  return apiFetch<void>(`/documents/${id}/purge/`, { method: "DELETE" });
}

/** GET /documents/:id/versions/ — all revisions of a document, newest first. */
export async function listVersions(id: string): Promise<ClientDocument[]> {
  const body = await apiFetch<Envelope<ApiDocument[]>>(`/documents/${id}/versions/`);
  return body.data.map(toDocument);
}

/** POST /documents/bulk-move/ */
export async function bulkMove(ids: string[], folderId: string | null): Promise<number> {
  const body = await apiFetch<Envelope<{ moved: number }>>("/documents/bulk-move/", {
    method: "POST",
    json: { ids, folder: folderId },
  });
  return body.data.moved;
}

/** POST /documents/bulk-delete/ */
export async function bulkDelete(ids: string[]): Promise<number> {
  const body = await apiFetch<Envelope<{ deleted: number }>>("/documents/bulk-delete/", {
    method: "POST",
    json: { ids },
  });
  return body.data.deleted;
}

/**
 * POST /documents/bulk-download/ — 1 file or a small set streams a download; a
 * large set is queued (202) and filed back into the client's documents. Returns
 * `{ queued: true, message }` for the async path, or triggers a browser download
 * and returns `{ queued: false }` for the inline path.
 */
export async function bulkDownload(
  ids: string[],
): Promise<{ queued: boolean; message?: string }> {
  const response = await apiFetchResponse("/documents/bulk-download/", {
    method: "POST",
    json: { ids },
  });
  if (response.status === 202) {
    const text = await response.text();
    let message = "Your download is being prepared.";
    try {
      message = JSON.parse(text)?.data?.message ?? message;
    } catch {
      /* not JSON — keep default */
    }
    return { queued: true, message };
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  downloadBlob(match?.[1] ?? "documents.zip", blob);
  return { queued: false };
}

// --- folders (archive tree) -------------------------------------------------

interface ApiFolder {
  id: string;
  client: string;
  parent: string | null;
  name: string;
  system_key: string;
  is_system: boolean;
  position: number;
  document_count: number;
}

function toFolder(api: ApiFolder): DocumentFolder {
  return {
    id: api.id,
    client: api.client,
    parent: api.parent,
    name: api.name,
    systemKey: api.system_key,
    isSystem: api.is_system,
    position: api.position,
    documentCount: api.document_count,
  };
}

/** GET /documents/folders/?client= — the client's folder tree (system folders ensured). */
export async function listFolders(clientId: string): Promise<DocumentFolder[]> {
  const body = await apiFetch<Envelope<ApiFolder[]>>(
    `/documents/folders/?page_size=200&client=${clientId}`,
  );
  return body.data.map(toFolder);
}

/** POST /documents/folders/ */
export async function createFolder(
  clientId: string,
  name: string,
  parentId?: string | null,
): Promise<DocumentFolder> {
  const body = await apiFetch<Envelope<ApiFolder>>("/documents/folders/", {
    method: "POST",
    json: { client: clientId, name, parent: parentId ?? null },
  });
  return toFolder(body.data);
}

/** PATCH /documents/folders/:id/ — rename, reparent, or reposition a user folder. */
export async function updateFolder(
  id: string,
  changes: { name?: string; parent?: string | null; position?: number },
): Promise<DocumentFolder> {
  const body = await apiFetch<Envelope<ApiFolder>>(`/documents/folders/${id}/`, {
    method: "PATCH",
    json: changes,
  });
  return toFolder(body.data);
}

export function renameFolder(id: string, name: string) {
  return updateFolder(id, { name });
}

/** POST /documents/folders/reorder/ — set folder positions from an ordered id list. */
export function reorderFolders(ids: string[]): Promise<void> {
  return apiFetch<void>("/documents/folders/reorder/", {
    method: "POST",
    json: { ids },
  });
}

/** DELETE /documents/folders/:id/ — must be empty and not a system folder. */
export function deleteFolder(id: string): Promise<void> {
  return apiFetch<void>(`/documents/folders/${id}/`, { method: "DELETE" });
}

/**
 * Fetch a document file (authenticated, firm-scoped stream — never a public URL)
 * and open it in a new tab. Uses `apiFetchBlob` so it shares the Bearer-token
 * attach and 401-refresh retry with the rest of the API layer.
 */
export async function openDocument(id: string): Promise<void> {
  const blob = await apiFetchBlob(`/documents/${id}/download/`);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
